import React, { useState, useRef, useEffect } from "react";
import { ItemValue, update_tiger_widgets, GraphValue, update_tiger_graph, resize_tiger_graph, prune_tiger_graphs, clear_tiger_graph, setWidgetRunStateLive, hideWidgetTooltip } from "./TigerWidget";
import { ContextMenu, ContextMenuItem } from "./ContextMenu";
import { parseGraphableTags, getTrendSuffixes, bucketViewTags, splitTagsByConfig, ANALOG_SUFFIXES, DIGITAL_SUFFIXES } from "../utils/graphableTags";
import { parseGraphParams } from "../utils/graphParams";
import { buildTagGraphParams, trendDaysWindow, TigerTimeWindow } from "../utils/buildTagGraphParams";
import { openGraphWindow, openDiagnosticsWindow, GraphMode, isGraphMode } from "../App";
import { startTagDrag, cancelTagDrag, dropTagAt } from "../utils/tagDrag";
import { startTiger } from "../utils/startTiger";
import { onReplayEvent } from "../utils/replayEvents";
import { onViewModeChange } from "../utils/useFollowViewMode";
import { getTigerResource } from "../utils/tigerResource";
import { getReplayStatus } from "../utils/replayControls";
import { currentMultiTimePlotSelections, type CurrentMultiTimeSelection } from "../utils/currentMultiTimeGraphs";
import { GraphTooltip } from "./GraphTooltip";
import { buildGraphTooltipSeed, resolveGraphTooltip, type GraphPlotTip } from "../utils/graphTooltip";

/** The 60 s graph poll only runs in live mode; replay freezes the view. */
export function shouldPoll(live: boolean): boolean {
  return live;
}

/** Hover dwell before an embedded multi-time graph's tooltip appears, matching
 *  the delay of a native tooltip rather than popping up the instant the cursor
 *  crosses the graph. */
const GRAPH_TIP_DELAY_MS = 1000;

function parseInlineStyle(css: string, fontScale: number = 1): React.CSSProperties {
  const result: Record<string, string> = {};
  for (const decl of css.split(";")) {
    const colon = decl.indexOf(":");
    if (colon === -1) continue;
    const prop = decl.slice(0, colon).trim();
    let value = decl.slice(colon + 1).trim();
    if (!prop || !value) continue;
    // Scale font-size pixel values
    if (prop === "font-size" && fontScale !== 1) {
      const m = value.match(/^([\d.]+)px$/);
      if (m) value = Math.round(parseFloat(m[1]) * fontScale) + "px";
    }
    // Convert kebab-case to camelCase
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    result[camel] = value;
  }
  return result as React.CSSProperties;
}

export interface TigerViewElement {
  id: string;
  class_name: string;
  x: number;
  y: number;
  width: number | null;
  height: number | null;
  text: string| null;
  color: string | null;
  background_color: string | null;
  extra_style: string | null;
  tiger_func: string | null;
  func_params: string | null;
  /** Set only for graph elements: backend-built params string for cloning into a dialog graph. */
  graph_tags_param?: string | null;
  /** Set only for graph elements: dialog window mode (matches openGraphWindow union). */
  graph_mode?: string | null;
  /** Pipe-separated list of `config:tag` identifiers; drives the tag-widget
   *  right-click menu. Empty / null → no menu. */
  graphable_tags?: string | null;
}

// Quoted TEXT widget labels (e.g. `tag[209]=TEXT 78 74 #303030 " °C"`) carry
// intentional spacing — a leading space, or runs of spaces for alignment. The
// backend preserves those spaces, but the browser collapses literal U+0020
// under the default `white-space: normal`, dropping the leading/repeated ones.
// Render them as non-breaking spaces so the exact spacing survives, matching
// the `&nbsp;` substitution the value widgets (ANALOG_BOX/DIGITAL_BOX) use.
function preserveWidgetSpaces(text: string | null): string | null {
  return text == null ? text : text.replace(/ /g, "\u00A0");
}

// ANALOG_BOX-style value boxes (ANALOG_BOX, ANALOG_DIFF_BOX, EXHAUST_CAN)
// arrive with `color` holding the same `background|value|description` colour
// triple the widget tick paints with, and func_params starting with the box
// description and unit. Render the creation text as the same three spans the
// tick builds so the box shows its configured colours from the first frame
// instead of the .TABS CSS defaults. Returns null for any other element.
function analogBoxInitialSpans(el: TigerViewElement): React.ReactNode | null {
  if (el.tiger_func !== "ANALOG_BOX" || !el.color || !el.color.includes("|")) return null;
  const [, valueColour, descrColour] = el.color.split("|");
  const params = (el.func_params ?? "").split("|");
  const descr = params[0] ?? "";
  const unit = params[1] ?? "";
  const text = el.text ?? "";
  let value = text.startsWith(descr) ? text.slice(descr.length) : text;
  let unitPart = "";
  if (unit !== "" && value.endsWith(" " + unit)) {
    unitPart = value.slice(value.length - unit.length - 1);
    value = value.slice(0, value.length - unit.length - 1);
  }
  return (
    <>
      <span style={{ color: descrColour }}>{preserveWidgetSpaces(descr)}</span>
      <span style={{ color: valueColour }}>{preserveWidgetSpaces(value)}</span>
      <span style={{ color: descrColour }}>{preserveWidgetSpaces(unitPart)}</span>
    </>
  );
}

interface TigerViewItems {
  image: string;
  /** View background colour (C++ default_view_background_colour), theme-mapped. */
  background_color?: string;
  /** Design size ([Interface Definition] default_view_width/height): the
   * scaling reference for a view without a background picture. */
  view_width?: number;
  view_height?: number;
  widget_elements: TigerViewElement[];
  graph_elements: TigerViewElement[];
}

const FALLBACK_LAYOUT: TigerViewItems = {
  image: "/images/background.png",
  background_color: "",
  widget_elements: [],
  graph_elements: [],
};

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

async function fetchUpdates(viewId?: string): Promise<ItemValue[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      return await invoke<ItemValue[]>("update_layout_items");
    }
    const url = viewId
      ? `/api/update_layout_items?viewId=${encodeURIComponent(viewId)}`
      : "/api/update_layout_items";
    const response = await fetch(url);
    if (!response.ok) return [];
    const text = await response.text();
    return text ? JSON.parse(text) : [];
  } catch {
    return [];
  }
}

/// Tauri mode: kicks off background fetches; individual graphs arrive via the
/// `graph-update` event as each one completes (no return value).
/// Webserver mode: fetches the full batch synchronously over HTTP.
async function triggerGraphUpdates(): Promise<GraphValue[]> {
  try {
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke<void>("update_graph_items");
      return [];
    }
    const response = await fetch("/api/update_graph_items");
    if (!response.ok) return [];
    const text = await response.text();
    return text ? JSON.parse(text) : [];
  } catch {
    return [];
  }
}

/// Refetch only specific plots of specific graphs, streaming each back via the
/// `graph-update` event like `triggerGraphUpdates`. Used by the replay poll to
/// refresh only the CURRENT multi-time plots — the graph's static plots are not
/// re-requested (the index-based merge keeps their last-rendered data).
/// Tauri-only — the replay poll never runs in webserver mode.
async function triggerGraphUpdatesForPlots(selections: CurrentMultiTimeSelection[]): Promise<void> {
  if (selections.length === 0 || !isTauri()) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke<void>("update_graph_items_plots", { selections });
  } catch {
    /* best-effort: a failed poll just leaves the last window shown */
  }
}

interface TigerViewPanelProps {
  viewId: string | null;
  /** Called with the deduped `config:tag` list whenever the displayed layout
   *  changes. Used by Tools → Data Grid to seed the grid with the tags
   *  currently on screen. Receives an empty array when no view is loaded. */
  onTagsChange?: (tags: string[]) => void;
}

function buildMenuItemsFor(el: TigerViewElement): ContextMenuItem[] {
  // Existing GRAPH path — preserved verbatim.
  if (el.tiger_func === "GRAPH" && el.graph_tags_param) {
    const mode: GraphMode = isGraphMode(el.graph_mode) ? el.graph_mode : "graph";
    const params = el.graph_tags_param;
    return [{ label: getTigerResource("TIGER_VIEW_SHOW_GRAPH"), onSelect: () => openGraphWindow(mode, params) }];
  }

  // Tag-widget path — driven by graphable_tags + tiger_func. Mirrors the C++
  // view_area_c::OnRButtonDown tag menu (TigerMainX.cpp): Display Graph, the
  // live + 30/180/365-day historical trends, and Display Diagnostics.
  const tags = parseGraphableTags(el.graphable_tags);
  if (tags.length === 0) return [];

  const items: ContextMenuItem[] = [{
    label: getTigerResource("TIGERUI_DISPLAY_GRAPH"),
    onSelect: () => openGraphWindow("graph", buildTagGraphParams(tags, "graph")),
  }];

  const trendSuffixes = getTrendSuffixes(el.tiger_func);
  if (trendSuffixes.length > 0) {
    // Live trend — C++ create_trend_dialog(0).
    items.push({
      label: getTigerResource("TIGERUI_DISPLAY_TREND"),
      onSelect: () => openGraphWindow("trend", buildTagGraphParams(tags, "trend", trendSuffixes)),
    });
    // Historical trends — C++ create_trend_dialog(N): window = [now − N days, now].
    // `now` is resolved at click time so the range tracks the current data.
    const historicalTrend = (label: string, days: number): ContextMenuItem => ({
      label,
      onSelect: () =>
        openGraphWindow(
          "trend",
          buildTagGraphParams(tags, "trend", trendSuffixes, trendDaysWindow(days)),
        ),
    });
    items.push(historicalTrend(getTigerResource("TIGERUI_DISPLAY_TREND_30"), 30));
    items.push(historicalTrend(getTigerResource("TIGERUI_DISPLAY_TREND_180"), 180));
    items.push(historicalTrend(getTigerResource("TIGERUI_DISPLAY_TREND_365"), 365));
  }

  // Display Diagnostics — per-tag query on the first tag's config:channel,
  // mirroring C++ create_diagnostics_dialog → set_tag_query.
  const first = tags[0];
  const colon = first.indexOf(":");
  if (colon > 0) {
    const config = first.slice(0, colon);
    const channel = first.slice(colon + 1);
    items.push({
      label: getTigerResource("TIGERUI_DISPLAY_DIAG"),
      onSelect: () => openDiagnosticsWindow(config, channel),
    });
  }
  return items;
}

// Empty-background menu — mirrors the C++ view_area_c::display_graph_set_menu
// (TigerMainX.cpp), shown when the right-click lands where no widget sits. Each
// item plots EVERY tag in the view, split into separate analog/digital windows
// (the C++ OnDisplayGraphSetButton/OnDisplayTrendSetButton open one dialog per
// channel type). Returns [] when the view has no classifiable tags, so no menu
// opens on an empty view.
function buildBackgroundMenuItems(elements: TigerViewElement[]): ContextMenuItem[] {
  const { analog, digital } = bucketViewTags(elements);
  if (analog.length === 0 && digital.length === 0) return [];

  // Open one window per turbine per type — analog windows first (in the
  // view's turbine order), then digital.
  const openSet = (mode: "graph" | "trend", window?: TigerTimeWindow) => () => {
    for (const group of splitTagsByConfig(analog)) {
      openGraphWindow(mode, buildTagGraphParams(group, mode, ANALOG_SUFFIXES, window));
    }
    for (const group of splitTagsByConfig(digital)) {
      openGraphWindow(mode, buildTagGraphParams(group, mode, DIGITAL_SUFFIXES, window));
    }
  };

  return [
    { label: getTigerResource("TIGERUI_DISPLAY_GRAPH_SET"), onSelect: openSet("graph") },
    { label: getTigerResource("TIGERUI_DISPLAY_TREND_SET"), onSelect: openSet("trend") },
    { label: getTigerResource("TIGERUI_DISPLAY_TREND_SET_30"), onSelect: openSet("trend", trendDaysWindow(30)) },
    { label: getTigerResource("TIGERUI_DISPLAY_TREND_SET_180"), onSelect: openSet("trend", trendDaysWindow(180)) },
    { label: getTigerResource("TIGERUI_DISPLAY_TREND_SET_365"), onSelect: openSet("trend", trendDaysWindow(365)) },
  ];
}

export function TigerViewPanel({ viewId, onTagsChange }: TigerViewPanelProps) {
  // Use a state-backed ref so the ResizeObserver effect re-runs whenever the
  // container `<div>` is (re)mounted -- e.g. after `setLayout(null)` unmounts the
  // panel on view switch and `setLayout(data)` re-mounts it. A plain useRef
  // doesn't trigger re-renders, so an observer attached to the first-mount node
  // would keep observing that detached node after the re-mount, silently
  // ignoring every window resize on the new view.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<TigerViewItems | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [rendered, setRendered] = useState<{ w: number; h: number; ox: number; oy: number }>({ w: 0, h: 0, ox: 0, oy: 0 });
  const [menu, setMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null);

  // Whether a replay is in progress (playing or paused). Drives the CURRENT
  // multi-time replay poll below: those graphs' "current" window tracks the
  // advancing replay position, so they re-fetch every 60s until the replay ends.
  const [replayActive, setReplayActive] = useState(false);

  // Hover tooltip for embedded graphs: cursor point + per-plot rows.
  const [graphTip, setGraphTip] = useState<{ x: number; y: number; plots: GraphPlotTip[] } | null>(null);
  // Resolved tooltip rows cached per graph-element id (descriptions/windows are async).
  const graphTipCacheRef = useRef<Map<string, GraphPlotTip[]>>(new Map());
  // The graph-element id currently hovered, so an async resolve only applies if
  // the cursor is still over the same graph when it completes.
  const graphTipHoverRef = useRef<string | null>(null);
  // Pending show timer (hover dwell) and the latest cursor position, so the
  // tooltip appears after GRAPH_TIP_DELAY_MS at wherever the cursor came to rest.
  const graphTipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graphTipPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const isDraggingRef = useRef(false);

  // Graph ids (tag0, tag1, …) are reused across views, so drop the resolved
  // tooltip cache and dismiss any open tooltip when the layout changes.
  useEffect(() => {
    graphTipCacheRef.current.clear();
    graphTipHoverRef.current = null;
    if (graphTipTimerRef.current !== null) {
      clearTimeout(graphTipTimerRef.current);
      graphTipTimerRef.current = null;
    }
    setGraphTip(null);
    // The widget tooltip lives on <body>; dismiss it too so it can't linger
    // over the new layout if a widget was unmounted while hovered.
    hideWidgetTooltip();
  }, [layout]);

  // Cancel any pending tooltip timer on unmount.
  useEffect(() => () => {
    if (graphTipTimerRef.current !== null) clearTimeout(graphTipTimerRef.current);
  }, []);

  useEffect(() => {
    if (!viewId) return;

    let cancelled = false;
    setLayout(null);

    async function load() {
      try {
        let data: TigerViewItems;
        if (isTauri()) {
          const { invoke, convertFileSrc } = await import("@tauri-apps/api/core");
          data = await invoke<TigerViewItems>("load_tiger_view", { viewId });
          if (data.image) {
            const enc = encodeURIComponent(data.image);
            // The custom-scheme origin is platform-specific:
            // http://tigerimg.localhost/ on Windows, tigerimg://localhost/ on
            // Linux (WebKitGTK). convertFileSrc("", scheme) yields the right
            // one; a hard-coded Windows form left every view background blank
            // on Linux.
            data = { ...data, image: `${convertFileSrc("", "tigerimg")}?path=${enc}` };
          }
        } else {
          const response = await fetch(`/api/load_tiger_view?viewId=${encodeURIComponent(viewId!)}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const text = await response.text();
          if (!text) throw new Error("empty response");
          data = JSON.parse(text);
          if (data.image) {
            const enc = encodeURIComponent(data.image);
            data = { ...data, image: `/api/tiger-image?path=${enc}` };
          }
        }
        if (!cancelled) setLayout(data);
      } catch {
        if (!cancelled) setLayout(FALLBACK_LAYOUT);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [viewId]);

  // Publish the deduped `config:tag` list whenever the layout changes.
  // Two sources: tag widgets expose `graphable_tags` (pipe-separated), and
  // graph elements expose `graph_tags_param` (a full params string we parse
  // with parseGraphParams to extract `plot[i].tag_name` — plus xTagName/yTagName
  // for XY graphs). Used by Tools → Data Grid to seed the new window.
  useEffect(() => {
    if (!onTagsChange) return;
    if (!layout) { onTagsChange([]); return; }
    const seen = new Set<string>();
    const tags: string[] = [];
    const push = (t: string | undefined) => {
      if (!t || !t.includes(":") || seen.has(t)) return;
      seen.add(t);
      tags.push(t);
    };
    for (const el of layout.widget_elements) {
      for (const t of parseGraphableTags(el.graphable_tags ?? "")) push(t);
    }
    // Trend-graph tags carry a `(AVG)`/`(MAX)`/`(MIN)` suffix per plot — the
    // base channel is what the data grid can actually look up, so we strip
    // anything from the first `(` onward for trend modes.
    const stripTrendSuffix = (t: string | undefined): string | undefined => {
      if (!t) return t;
      const i = t.indexOf("(");
      return i >= 0 ? t.slice(0, i) : t;
    };
    for (const el of [...layout.widget_elements, ...layout.graph_elements]) {
      if (!el.graph_tags_param) continue;
      const isTrend =
        el.graph_mode === "trend" || el.graph_mode === "xyTrend" || el.graph_mode === "xyTrendMulti";
      const normalize = isTrend ? stripTrendSuffix : (t: string | undefined) => t;
      for (const p of parseGraphParams(el.graph_tags_param).plots) {
        push(normalize(p.tagName));
        push(normalize(p.xTagName));
        push(normalize(p.yTagName));
      }
    }
    onTagsChange(tags);
  }, [layout, onTagsChange]);

  const scale = natural ? rendered.w / natural.w : 1;
  const ox = Math.round(natural ? rendered.ox : 0);
  const oy = Math.round(natural ? rendered.oy : 0);

  useEffect(() => {
    if (!layout) return;
    let cancelled = false;

    // Re-apply widget updates immediately on resize with fresh scale values
    const reapply = () => {
      fetchUpdates(viewId ?? undefined).then((updates) => {
        if (cancelled) return;
        [...(layout?.widget_elements ?? []), ...(layout?.graph_elements ?? [])].forEach((el) => {
          const upd = updates.find((u) => String(u.id) === el.id);
          if (upd) {
            update_tiger_widgets(el, upd, ox, oy, scale);
          } else if (el.tiger_func === "GRAPH") {
            update_tiger_widgets(el, { id: 0, t: "", v: [], c: "" }, ox, oy, scale);
          }
        });
      });
    };

    reapply();
    reapplyRef.current = reapply;
    const interval = setInterval(reapply, 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      reapplyRef.current = null;
    };
  }, [layout, rendered]);

  // Synchronously resize every graph's uPlot canvas whenever the render scale
  // changes, so window resizing works even when no new graph data is arriving.
  useEffect(() => {
    if (!layout) return;
    console.log(`graph-resize effect: scale=${scale}, natural=${JSON.stringify(natural)}, rendered=${JSON.stringify(rendered)}, graphs=${layout.graph_elements.length}`);
    for (const el of layout.graph_elements) {
      const w = Math.round((el.width ?? 300) * scale);
      const h = Math.round((el.height ?? 100) * scale);
      resize_tiger_graph(el.id, w, h);
    }
  }, [layout, rendered]);

  // On view switch, destroy every uPlot instance (and its cached series) so the
  // next view starts fresh. Graph ids like `tag0`, `tag1`, ... are reused across
  // views, so keeping entries whose ids happen to appear in the new layout would
  // bleed stale series (sometimes from an entirely different model/channel) into
  // the new screen and show them as extra, wrong-coloured plots.
  useEffect(() => {
    if (!layout) return;
    prune_tiger_graphs(new Set<string>());
  }, [layout]);

  // Global cancel listeners for tag drag: cancel on mouseup, Escape, or window focus.
  useEffect(() => {
    const onMouseUp = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      void dropTagAt(e.screenX, e.screenY);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      void cancelTagDrag();
    };
    const onWindowFocus = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      void cancelTagDrag();
    };

    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("focus", onWindowFocus);

    let unlistenEnd: (() => void) | undefined;
    let cancelled = false;
    if (isTauri()) {
      void import("@tauri-apps/api/event").then(({ listen }) =>
        listen("tag-drag-end", () => {
          isDraggingRef.current = false;
          document.body.style.cursor = "";
        })
      ).then(off => {
        if (cancelled) off(); else unlistenEnd = off;
      });
    }

    return () => {
      cancelled = true;
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("focus", onWindowFocus);
      unlistenEnd?.();
    };
  }, []);

  // Keep the current render transform in a ref so callbacks in the graph-refresh
  // effect can read fresh values without being re-captured (otherwise the effect
  // would have to list `[rendered]` in its deps, which would re-run it -- and
  // re-invoke `update_graph_items` -- on every ResizeObserver tick, piling up
  // duplicate requests).
  const renderTransformRef = useRef({ ox, oy, scale });
  renderTransformRef.current = { ox, oy, scale };

  const liveRef = useRef(true);
  const refreshRef = useRef<((force?: boolean) => void) | null>(null);
  // Immediate widget re-fetch, used to refresh the view the instant the
  // Live/Replay switch flips (rather than waiting for the next 1s tick).
  const reapplyRef = useRef<(() => void) | null>(null);

  // The view's CURRENT multi-time plots (per graph: id + plot indices), recomputed
  // on layout change. Held in a ref so the mount-time replay effects (poll +
  // finished handler) read the current set without re-subscribing.
  const currentMtSelectionsRef = useRef<CurrentMultiTimeSelection[]>([]);
  useEffect(() => {
    currentMtSelectionsRef.current = layout ? currentMultiTimePlotSelections(layout.graph_elements) : [];
  }, [layout]);

  // Refresh graph data every 60 seconds. In Tauri mode, the backend streams each
  // graph back as a `graph-update` event as its data arrives, so individual graphs
  // render as soon as they're ready. In webserver mode, we still poll a batch HTTP
  // endpoint.
  useEffect(() => {
    if (!layout || layout.graph_elements.length === 0) return;
    let cancelled = false;
    let unlisten: (() => void) | null = null;

    // Dynamic graph/trend thumbnail views (DYN_VIEW_GRAPH_n / DYN_TREND_VIEW_GRAPH_n)
    // render as compact previews: drop the numeric x/y scale values.
    const isThumbnailView = !!viewId && viewId.includes("_VIEW_GRAPH_");

    const applyGraph = (gv: GraphValue) => {
      const el = layout.graph_elements.find((e) => e.id === gv.id);
      if (!el) return;
      const { ox, oy, scale } = renderTransformRef.current;
      update_tiger_graph(el, gv, ox, oy, scale, isThumbnailView);
    };

    // `force` bypasses the live-only gate: the 60s timer polls in live only, but a
    // live/replay switch must fetch the new mode's data once (the backend serves
    // ONLINE/OFFLINE per the run state). Replay's data is then static until the
    // next switch — matching the standalone graph windows.
    const refreshGraphs = (force = false) => {
      console.log(`[DIAG refreshGraphs] viewId=${viewId} force=${force} live=${liveRef.current} graphs=${layout.graph_elements.length}`);
      if (!force && !shouldPoll(liveRef.current)) return; // 60s poll is live-only
      // A forced refresh is a full-replacement refetch (Live/Replay switch or a
      // freshly loaded replay window): the new mode's data replaces whatever is
      // on screen. Clear each graph first -- drop its cached series AND repaint it
      // empty -- before requesting the new data, so the outgoing mode's plot is
      // wiped at once instead of lingering until the refetch returns (and an empty
      // OFFLINE reply, e.g. switching to Replay before any replay is loaded, stays
      // empty rather than merge_graph_series keeping the stale live data). The
      // live-only 60s poll (force=false) leaves the cache/plot intact so
      // progressive/partial updates still merge.
      if (force) {
        const { scale } = renderTransformRef.current;
        for (const el of layout.graph_elements) clear_tiger_graph(el, scale, isThumbnailView);
      }
      triggerGraphUpdates().then((graphValues) => {
        if (cancelled) return;
        // Webserver mode: the batch comes back here and we update each graph.
        // Tauri mode: graphValues is empty; individual graphs arrive via the event listener.
        graphValues.forEach(applyGraph);
      });
    };
    refreshRef.current = refreshGraphs;

    let interval: ReturnType<typeof setInterval> | null = null;

    // In Tauri mode, register the `graph-update` listener BEFORE kicking off the
    // first refresh -- otherwise early progress events can fire before `listen`
    // finishes registering and be missed entirely.
    if (isTauri()) {
      (async () => {
        const { listen } = await import("@tauri-apps/api/event");
        if (cancelled) return;
        const off = await listen<GraphValue>("graph-update", (ev) => {
          if (cancelled) return;
          applyGraph(ev.payload);
        });
        if (cancelled) { off(); return; }
        unlisten = off;
        // Forced: the first fetch on a layout change is a full-replacement
        // refetch for the new view and must run in BOTH modes (ONLINE in live,
        // OFFLINE/replay data in replay). The live-only gate only applies to the
        // 60s poll below; without forcing here, switching views while already in
        // replay would leave the new view's embedded graphs blank -- the
        // view-mode-changed / replay-data-loaded forced refreshes don't fire on a
        // plain view switch.
        refreshGraphs(true);
        interval = setInterval(refreshGraphs, 60_000);
      })();
    } else {
      // Webserver mode has no Live/Replay wiring (those events are Tauri-only), so
      // it is always live and the non-forced initial fetch already runs.
      refreshGraphs();
      interval = setInterval(refreshGraphs, 60_000);
    }

    return () => {
      cancelled = true;
      refreshRef.current = null;
      if (interval !== null) clearInterval(interval);
      if (unlisten) unlisten();
    };
  }, [layout]);

  // Follow the global Live/Replay switch: pause polling in replay, and refresh
  // immediately when returning to live. Works on desktop (the Tauri
  // `view-mode-changed` event) and on the web build, where the webserver emits
  // no events and the menu-bar switch re-broadcasts on the "tiger-view-mode"
  // BroadcastChannel (see `viewMode.setViewMode`) — without the web branch the
  // STATUS widget stayed labelled "Live" and the 60s poll kept running in replay.
  useEffect(() => onViewModeChange((live, isInitialSync) => {
    // liveRef gates the live-only 60s poll, so it must follow the mode (a replay
    // session started before mount must not keep polling as if live), and the
    // STATUS widget label follows too.
    liveRef.current = live;
    setWidgetRunStateLive(live);
    // reapply on both the mount sync and a real switch. Optional-chain no-ops if a
    // layout change is mid-teardown (the [layout] effect fires its own initial fetch).
    reapplyRef.current?.();
    // Only a genuine flip refreshes the data for the new mode (C++ Ui_update_tiger_views
    // runs on every Ui_change_dv_run_state); on mount the [layout] effect already
    // issues the initial fetch. Embedded graphs are forced (true) so they fetch the
    // new mode's data even when switching to replay (the 60s poll stays live-only).
    // onViewModeChange's web path POLLS get_view_run_state per-session, so this also
    // catches a flip this (possibly backgrounded) panel missed on the BroadcastChannel.
    if (!isInitialSync) refreshRef.current?.(true);
  }), []);

  // Refresh the widgets on every replay sample (C++ updates the views per sample),
  // so replay playback tracks the sample rate at higher speeds instead of being
  // capped at the 1 s widget poll. Additionally, when a replay window finishes
  // loading (`replay-data-loaded`), force every embedded graph to refetch so the
  // newly loaded data shows at once — the `view-mode-changed` graph refresh fires
  // before the async load completes, and the 60 s poll is live-only, so without
  // this the graphs would stay blank/stale until a manual mode toggle.
  useEffect(() => {
    // onReplayEvent wraps the Tauri events in the app and polls get_replay_status
    // in the browser (the webserver emits no events), so the -live web tab gets the
    // same per-sample widget refresh and forced graph refetch on window load.
    const offTick = onReplayEvent("replay-tick", () => { reapplyRef.current?.(); });
    const offLoaded = onReplayEvent("replay-data-loaded", () => {
      reapplyRef.current?.();
      refreshRef.current?.(true); // forced: refetch graphs for the loaded replay window
    });
    return () => { offTick(); offLoaded(); };
  }, []);

  // Track whether a replay is in progress so CURRENT multi-time graphs can poll
  // while it runs. Driven by replay-started (→true) / replay-finished (→false),
  // with a mount sync via getReplayStatus for a view opened mid-replay. On
  // replay-finished, one final refetch so the end-of-replay window shows (the
  // poll stops as soon as replayActive flips false). Mirrors GraphWindow.
  useEffect(() => {
    let cancelled = false;
    getReplayStatus().then((s) => { if (!cancelled) setReplayActive(!!s?.active); }).catch(() => {});
    const offStarted = onReplayEvent("replay-started", () => setReplayActive(true));
    const offFinished = onReplayEvent("replay-finished", () => {
      setReplayActive(false);
      void triggerGraphUpdatesForPlots(currentMtSelectionsRef.current);
    });
    return () => { cancelled = true; offStarted(); offFinished(); };
  }, []);

  // While a replay runs, re-fetch the view's CURRENT multi-time plots every 60s
  // (wall-clock, matching the live poll) so their "current" window follows the
  // advancing replay position. Only those plots refetch — the graphs' static
  // RELATIVE / HISTORICAL plots stay frozen. Stops when the replay
  // finishes (replayActive → false). Tauri-only; webserver never sets replayActive.
  useEffect(() => {
    if (!replayActive) return;
    const id = setInterval(() => {
      void triggerGraphUpdatesForPlots(currentMtSelectionsRef.current);
    }, 60_000);
    return () => clearInterval(id);
  }, [replayActive]);

  const imageRef = useRef<string | null>(null);

  useEffect(() => {
    if (!layout) return;
    // Compute a fallback `natural` from the union of widget + graph bounding boxes,
    // so views whose background image fails to load still scale correctly. Without
    // this, `natural` would stay at whatever the previous view set (or null) and
    // the ResizeObserver would never attach / fire for this view, meaning the
    // layout only catches up to the new window size when the user switches views.
    const layoutNatural = () => {
      // Preferred: the view's design size from the ini (C++ sizes its window
      // from the same keys), so a picture-less view keeps the aspect ratio
      // and coordinate space the designer laid it out in.
      if (layout.view_width && layout.view_height && layout.view_width > 0 && layout.view_height > 0) {
        return { w: layout.view_width, h: layout.view_height };
      }
      let maxW = 0;
      let maxH = 0;
      for (const el of [...layout.widget_elements, ...layout.graph_elements]) {
        const right = el.x + (el.width ?? 0);
        const bottom = el.y + (el.height ?? 0);
        if (right > maxW) maxW = right;
        if (bottom > maxH) maxH = bottom;
      }
      return { w: maxW || 1920, h: maxH || 1080 };
    };

    if (!layout.image) {
      setNatural(layoutNatural());
      imageRef.current = "";
      return;
    }
    if (layout.image === imageRef.current) return;
    imageRef.current = layout.image;
    const img = new Image();
    img.onload = () => {
      console.log(`bg image loaded: src=${layout.image} -> naturalWidth=${img.naturalWidth} naturalHeight=${img.naturalHeight}`);
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = (e) => {
      console.error(`bg image FAILED to load: src=${layout.image}`, e);
      // Fall back to a bounding box derived from the layout so resize still works.
      setNatural(layoutNatural());
    };
    img.src = layout.image;
  }, [layout]);

  useEffect(() => {
    if (!natural || !containerEl) return;

    const update = () => {
      const cw = containerEl.clientWidth;
      const ch = containerEl.clientHeight;
      const scale = Math.min(cw / natural.w, ch / natural.h);
      const w = Math.round(natural.w * scale);
      const h = Math.round(natural.h * scale);
      const ox = Math.round((cw - w) / 2);
      const oy = Math.round((ch - h) / 2);
      setRendered(prev =>
        prev.w === w && prev.h === h && prev.ox === ox && prev.oy === oy
          ? prev
          : { w, h, ox, oy }
      );
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, [natural, containerEl]);

  if (!layout) return null;

  // Right-click on empty background → the Graph/Trend Set menu. Widgets carry
  // their own onContextMenu (which doesn't stopPropagation), so guard against
  // their bubbled events by only acting when the target is the container itself
  // or the background <img> — mirrors the C++ "no widget under cursor" branch.
  const onBackgroundContextMenu = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target !== e.currentTarget && target.tagName !== "IMG") return;
    const items = buildBackgroundMenuItems(layout.widget_elements);
    if (items.length === 0) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  return (
    <div
      ref={setContainerEl}
      onContextMenu={onBackgroundContextMenu}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        // View background colour (C++ default_view_background_colour) fills the
        // area behind / around the (letterboxed) background image.
        backgroundColor: layout.background_color || undefined,
        // Establish the view base font (matches the .TDS class and the C++
        // interface). Widgets inherit this unless their extra_style overrides
        // font-family; without it they fall back to the global sans-serif body
        // font and render in the wrong typeface.
        fontFamily: '"Courier New", monospace',
        fontSize: `${Math.round(11.5 * scale)}px`,
      } as React.CSSProperties}
    >
      {layout.image && (
        <img
          src={layout.image}
          style={{
            position: "absolute",
            left: rendered.ox,
            top: rendered.oy,
            width: rendered.w,
            height: rendered.h,
          }}
        />
      )}
      {[...layout.widget_elements, ...layout.graph_elements].map((el, idx) => {
        const style: React.CSSProperties = {
          position: "absolute",
          left: Math.round(ox + el.x * scale),
          top: Math.round(oy + el.y * scale),
        };
        if (el.width != null) style.width = Math.round(el.width * scale);
        if (el.height != null) style.height = Math.round(el.height * scale);
        const boxSpans = analogBoxInitialSpans(el);
        // For a value box `color` is the colour triple, not a CSS colour.
        if (el.color && boxSpans == null) style.color = el.color;
        if (el.background_color) style.backgroundColor = el.background_color;
        if (el.extra_style) Object.assign(style, parseInlineStyle(el.extra_style, scale));

        const items = buildMenuItemsFor(el);
        const onContextMenu = items.length > 0
          ? (e: React.MouseEvent) => {
              e.preventDefault();
              setMenu({ x: e.clientX, y: e.clientY, items });
            }
          : undefined;
        const onMouseDown = el.graphable_tags
          ? (e: React.MouseEvent) => {
              if (e.button !== 0) return;
              e.preventDefault();
              isDraggingRef.current = true;
              document.body.style.cursor = "grabbing";
              void startTagDrag({
                graphable_tags: el.graphable_tags!,
                tiger_func: el.tiger_func ?? "",
              }).catch(() => {
                // Drag failed to register on the Rust side — revert local state so we
                // don't leave a stuck "grabbing" cursor or ghost ref.
                isDraggingRef.current = false;
                document.body.style.cursor = "";
              });
            }
          : undefined;
        // Embedded graphs (any type) get a hover tooltip listing every plot with
        // its colour — plus, for multi-time plots, the start/end window —
        // mirroring the C++ tiger_graph::get_tooltip_str (one line per plot).
        // Shown instantly from the synchronous seed; descriptions and the real
        // windows resolve async and replace the rows in place (cached per id).
        const tipHandlers =
          el.tiger_func === "GRAPH" && el.graph_tags_param && el.id
            ? (() => {
                const id = el.id!;
                const params = el.graph_tags_param!;
                return {
                  onMouseEnter: (e: React.MouseEvent) => {
                    graphTipHoverRef.current = id;
                    graphTipPosRef.current = { x: e.clientX, y: e.clientY };
                    if (graphTipTimerRef.current !== null) clearTimeout(graphTipTimerRef.current);
                    // Wait for the hover to settle before showing the tooltip.
                    graphTipTimerRef.current = setTimeout(() => {
                      graphTipTimerRef.current = null;
                      if (graphTipHoverRef.current !== id) return;
                      const cached = graphTipCacheRef.current.get(id);
                      const plots = cached ?? buildGraphTooltipSeed(params);
                      const { x, y } = graphTipPosRef.current;
                      setGraphTip({ x, y, plots });
                      if (!cached) {
                        resolveGraphTooltip(params)
                          .then((resolved) => {
                            graphTipCacheRef.current.set(id, resolved);
                            if (graphTipHoverRef.current === id) {
                              setGraphTip((prev) => (prev ? { ...prev, plots: resolved } : prev));
                            }
                          })
                          .catch(() => {});
                      }
                    }, GRAPH_TIP_DELAY_MS);
                  },
                  // Track the resting cursor position so the (delayed) tooltip
                  // appears where the cursor is, not where it first entered.
                  onMouseMove: (e: React.MouseEvent) => {
                    graphTipPosRef.current = { x: e.clientX, y: e.clientY };
                  },
                  onMouseLeave: () => {
                    if (graphTipHoverRef.current === id) graphTipHoverRef.current = null;
                    if (graphTipTimerRef.current !== null) {
                      clearTimeout(graphTipTimerRef.current);
                      graphTipTimerRef.current = null;
                    }
                    setGraphTip(null);
                  },
                };
              })()
            : undefined;
        // ALARM_START_TIGER renders as a push button (matching the C++
        // CButton), not a text div. Clicking it launches a Tiger display for the
        // turbine config carried in func_params — see the alarm_start_tiger
        // command (port of alarm_start_tiger_widget::click_event).
        if (el.tiger_func === "ALARM_START_TIGER") {
          const configName = el.func_params;
          return (
            <button
              key={el.id ?? `idx-${idx}`}
              id={el.id ?? undefined}
              className={el.class_name}
              style={style}
              type="button"
              onContextMenu={onContextMenu}
              onClick={() => {
                startTiger(configName).catch((err) => {
                  console.error("alarm_start_tiger failed", err);
                });
              }}
            >
              {preserveWidgetSpaces(el.text)}
            </button>
          );
        }
        return (
          <div
            key={el.id ?? `idx-${idx}`}
            id={el.id ?? undefined}
            className={el.class_name}
            style={style}
            onContextMenu={onContextMenu}
            onMouseDown={onMouseDown}
            {...tipHandlers}
          >
            {boxSpans ?? preserveWidgetSpaces(el.text)}
          </div>
        );
      })}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={menu.items}
          onClose={() => setMenu(null)}
        />
      )}
      {graphTip && (
        <GraphTooltip x={graphTip.x} y={graphTip.y} plots={graphTip.plots} />
      )}
    </div>
  );
}
